import { Divider, Menu, MenuItem } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedClassKey } from "assets/jss/common-styles";
import ButtonEllipsis from "components/Buttons/ButtonEllipsis";
import DrawerTextInput from "components/Dialogs/DrawerTextInput";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import HtmlUtils from "lib/HtmlUtils";
import { FeedType, PublicationType } from "models/ApiEnums";
import { FeedItemBasePublicationDto, FeedItemDto, FeedItemInteractionDto, FeedItemPermissionsDto } from "models/ApiModels";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { RoutePaths } from "routes/RouteConfig";
import CreateQuestion from "views/Publication/CreateQuestion";
import PublicationLogDialog from "views/Publication/Dialogs/PublicationLogDialog";
import PublicationRelationshipsDialog from "views/Publication/Dialogs/PublicationRelationshipsDialog";
import { getPublicationUrl } from "views/Timeline/Components/FeedHelpers";
import AbuseReportDrawer from "views/Timeline/Dialogs/AbuseReportDrawer";
import PublicationHighlightDrawer, { PublicationHighlightDrawerResult } from "views/Timeline/Dialogs/PublicationHighlightDrawer";
import PublicationLikesReviewDrawer from "views/Timeline/Dialogs/PublicationLikesReviewDrawer";
import PublicationUnblockDrawer from "views/Timeline/Dialogs/PublicationUnblockDrawer";
import TranscriptionSubtitleDialog from "views/Timeline/Dialogs/TranscriptionSubtitleDialog";
import VideoSearchSubtitlesDrawer from "views/Timeline/Dialogs/VideoSearchSubtitlesDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    menuAnchorEl: HTMLElement;
    dialog: any;
}

interface IProps {
    data: FeedItemDto;
    feedType: FeedType;
    fullView: boolean;

    onEditPublication: () => void;
    onRemovePublication: () => void;
    onReloadPublication: () => void;

    onUpdateCard: (data: DeepPartial<FeedItemDto>) => void;

    onInfoRequest?: () => void;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class ActionsDefaultDropDown extends React.PureComponent<Props, IState> {

    private handlePublicationId: string;
    private localInteraction: FeedItemInteractionDto;
    private localPermissions: FeedItemPermissionsDto;
    private localPub: FeedItemBasePublicationDto;

    constructor(props: Props) {
        super(props);
        this.state = {
            menuAnchorEl: null,
            dialog: null
        };
    }

    ///
    private handleCloseDialog = () => {
        this.setState({ dialog: null });
    };

    /// 
    private handleSearchSubtitleAsset = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <VideoSearchSubtitlesDrawer
                publicationId={this.props.data.publicationId}
                fileId={this.props.data.body.attachs[0].mainFile.fileId}
                subtitle={this.props.data.publication.title}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    /// 
    private handleSearchSubtitleMeta = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <VideoSearchSubtitlesDrawer
                publicationId={this.props.data.publicationId}
                fileId={null}
                subtitle={this.props.data.publication.title}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    /// 
    private handleViewSubtitleAsset = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <TranscriptionSubtitleDialog
                publicationId={null}
                fileId={this.props.data.body.attachs[0].mainFile.fileId}
                subtitle={this.props.data.publication.title}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    /// 
    private handleViewSubtitleMeta = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <TranscriptionSubtitleDialog
                publicationId={this.props.data.publicationId}
                fileId={null}
                subtitle={this.props.data.publication.title}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private handleHighlightClose = (r: PublicationHighlightDrawerResult) => {
        const { t, data: { publicationId } } = this.props;

        this.handleCloseDialog();

        if (r != null) {
            api.addHighlightPublication(publicationId, r.days)
                .then((newHighlightUntil) => {
                    if (newHighlightUntil != null) {
                        this.props.onReloadPublication();
                        this.props.snackbar.showMessage(t("Common.Feed.Messages.HighlightSuccess", { highlightUntil: newHighlightUntil }), "success");
                    }
                });
        }
    };

    ///
    private handleLikeReviewClick = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationLikesReviewDrawer
                publicationId={this.handlePublicationId}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => {
        this.setState({ menuAnchorEl: e.currentTarget });
    };

    ///
    private handleBlock = () => {
        const { t, data: { publicationId } } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <DrawerTextInput
                    title={t("Common.Feed.Actions.BlockTitle")}
                    placeHolder={t("Common.Feed.Actions.BlockPlaceholder")}
                    confirmText={t("Common.Feed.Actions.BlockConfirmButton")}
                    rows={5}
                    maxLength={1500}
                    inputOptional={false}
                    onCancel={this.handleCloseDialog}
                    onAccept={
                        (text) => {
                            api.publicationSwapBlock(publicationId, text)
                                .then((result) => {
                                    if (result) {
                                        this.props.onReloadPublication();
                                        this.props.snackbar.showMessage(t("Common.Feed.Messages.BlockSuccess"), "success");
                                    }
                                    this.handleCloseDialog();
                                });
                        }
                    }
                />
            )
        });
    };

    ///
    private handleUnblock = () => {
        const { t, data: { publicationId, publication: { title } } } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <MuiConfirmation
                    title={t("Common.Feed.Actions.UnblockTitle")}
                    message={
                        <Trans
                            i18nKey="Common.Feed.Actions.UnblockMessage"
                            values={{ title }}
                            components={{ b: <strong /> }}
                        />
                    }
                    onClose={
                        (confirm) => {
                            if (confirm) {
                                api.publicationSwapBlock(publicationId, null)
                                    .then((result) => {
                                        if (result) {
                                            this.props.onReloadPublication();
                                            this.props.snackbar.showMessage(t("Common.Feed.Messages.UnblockSuccess"), "success");
                                        }
                                        this.handleCloseDialog();
                                    });

                            }
                            else
                                this.handleCloseDialog();
                        }
                    }
                />
            )
        });
    };

    ///
    private handleHighlight = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationHighlightDrawer onClose={this.handleHighlightClose} />
        });
    };

    ///
    private handleUndoHighlight = () => {
        const { t, data: { publicationId } } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <MuiConfirmation
                    title={t("Common.Feed.Actions.UndoHighlightTitle")}
                    message={t("Common.Feed.Actions.UndoHighlightMessage")}
                    onClose={
                        (confirm) => {
                            if (confirm) {

                                this.handleCloseDialog();

                                api.cancelHighlightPublication(publicationId)
                                    .then((canceled) => {
                                        if (canceled) {
                                            this.props.onReloadPublication();
                                            this.props.snackbar.showMessage(t("Common.Feed.Messages.UndoHighlightSuccess"), "success");
                                        }
                                    });

                            }
                            else
                                this.handleCloseDialog();
                        }
                    }
                />
            )
        });
    };

    ///
    private handleReportAbuse = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: (
                <AbuseReportDrawer
                    publicationId={this.handlePublicationId}
                    onClose={
                        (feedItem) => {
                            this.handleCloseDialog();

                            if (feedItem != null) {
                                if (this.handlePublicationId != this.props.data.publicationId)
                                    this.props.onUpdateCard({
                                        publicationId: this.props.data.publicationId,
                                        bestAnswer: {
                                            statistics: feedItem.statistics,
                                            interaction: feedItem.interaction,
                                        }
                                    });
                                else
                                    this.props.onUpdateCard({
                                        publicationId: this.props.data.publicationId,
                                        statistics: feedItem.statistics,
                                        interaction: feedItem.interaction,
                                    });
                            }
                        }
                    }
                />
            )
        });
    };

    ///
    private handleRequestUnblockPublication = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: (
                <PublicationUnblockDrawer
                    publicationId={this.handlePublicationId}
                    onClose={
                        (feedItem) => {
                            this.handleCloseDialog();

                            if (feedItem != null) {
                                this.props.onUpdateCard({
                                    publicationId: this.props.data.publicationId,
                                    statistics: feedItem.statistics,
                                    interaction: feedItem.interaction,
                                });
                            }
                        }
                    }
                />
            )
        });
    };

    ///
    private handleAudit = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationLogDialog
                publicationId={this.props.data.publicationId}
                onClose={this.handleCloseDialog}
            />
        });
    };

    ///
    private handleDownloadAsset = () => {
        const attach = this.props.data.body.attachs[0].mainFile;
        this.setState({ menuAnchorEl: null }, () =>
            HtmlUtils.download(`/Download/${attach.fileId}`, attach.metadata.filename)
        );
    };

    ///
    private handleEdit = () => {
        this.setState({ menuAnchorEl: null }, () =>
            this.props.onEditPublication()
        );
    };

    ///
    private handleDelete = () => {
        this.setState({ menuAnchorEl: null }, () =>
            this.props.onRemovePublication()
        );
    };

    ///
    private handleInfoRequest = () => {
        this.setState({ menuAnchorEl: null }, () =>
            this.props.onInfoRequest()
        );
    };

    ///
    private handleRelationships = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <PublicationRelationshipsDialog
                publicationId={this.props.data.publicationId}
                publicationTitle={this.props.data.publication.title}
                onClose={
                    (changed) => this.setState(
                        { dialog: null },
                        () => changed && this.props.onReloadPublication && this.props.onReloadPublication())
                }
            />
        });
    };

    ///
    private handleQrCode(publicationId: string) {
        this.setState({ menuAnchorEl: null }, () =>
            HtmlUtils.openUrlInNewWindow(RoutePaths.PublicationQrCode.replace(":id", publicationId))
        );
    }

    ///
    private handleCopyLink = () => {
        var url = this.props.data.bestAnswer == null
            ? window.location.origin + getPublicationUrl(this.props.data.publicationId, this.props.data.publicationType)
            : window.location.origin + getPublicationUrl(this.props.data.bestAnswer.publicationId, PublicationType.Answer, this.props.data.publicationId);;

        navigator.clipboard.writeText(url)
            .then(
                () => {
                    this.setState({
                        menuAnchorEl: null,
                    }, () => this.props.snackbar.showMessage(this.props.t("Message.LinkCopySuccess"), "info"));
                },
                (err) => {
                    this.setState({
                        menuAnchorEl: null,
                    }, () => this.props.snackbar.showMessage(err, "error"));
                });
    };

    ///
    private handleCreateQuestion = () => {
        this.setState({
            menuAnchorEl: null,
            dialog: <CreateQuestion
                createFromPublication={
                    {
                        publicationId: this.props.data.publicationId,
                        publicationType: this.props.data.publicationType,
                        title: this.props.data.publication.title,
                        authorName: this.props.data.publication.publisher.name,
                        thumbnailUrl: this.props.data.publication.attachMimeType?.startsWith("image") ? this.props.data.publication.attachUrl : null
                    }
                }
                onClose={this.handleCloseDialog}
            />
        });
    };

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null });
    }

    ///
    private buildDropDownMenu(): Array<any> {
        const {
            t,
            data: {
                publicationId,
                publicationType,
                body: {
                    attachs,
                    metadata
                },
                permissions,
                bestAnswer
            },
            feedType,

            onEditPublication,
            onInfoRequest,
            onRemovePublication
        } = this.props;

        const actionMenu: Array<any> = [];

        if (onEditPublication) {
            actionMenu.push(<MenuItem key="edit" onClick={this.handleEdit}>{t("Common.MenuAction.Edit")}</MenuItem>);
        }

        if (permissions.canEditRelationships && (publicationType != PublicationType.Question || this.props.fullView || bestAnswer == null)) {
            actionMenu.push(<MenuItem key="relationship" onClick={this.handleRelationships}>{t("Common.MenuAction.EditRelationships")}</MenuItem>);
        }

        //
        const arr0 = [];

        if (this.localPermissions.canBlock) {
            if (this.localPub.blocked)
                arr0.push(<MenuItem key="unblock" onClick={this.handleUnblock}>{t("Common.MenuAction.Unblock")}</MenuItem>);
            else
                arr0.push(<MenuItem key="block" onClick={this.handleBlock}>{t("Common.MenuAction.Block")}</MenuItem>);
        }

        if (this.localPermissions.canRequestUnblockPublication)
            arr0.push(<MenuItem key="requestUnblockPublication" onClick={this.handleRequestUnblockPublication}>{t("Common.MenuAction.RequestUnblockPublication")}</MenuItem>);

        if (this.localPermissions.canReportAbuse)
            arr0.push(<MenuItem key="reportAbuse" onClick={this.handleReportAbuse}>{t("Common.MenuAction.ReportAbuse")}</MenuItem>);

        if (this.localPermissions.canAddHighlight)
            arr0.push(<MenuItem key="highlight" onClick={this.handleHighlight}>{t("Common.MenuAction.Highlight")}</MenuItem>);
        if (this.localPermissions.canCancelHighlight)
            arr0.push(<MenuItem key="undohighlight" onClick={this.handleUndoHighlight}>{t("Common.MenuAction.UndoHighlight")}</MenuItem>);

        if (arr0.length > 0) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="dividerAdmin" />);
            actionMenu.push(arr0);
        }

        //
        const arr2 = [];

        if (feedType != FeedType.CourseAutoEnroll
            && feedType != FeedType.CourseEnrolled
            && feedType != FeedType.CourseViewer
        ) {
            if (attachs.length > 0 && (attachs[0].mainFile?.mediaAsset?.hasSubtitle ?? false)) {
                arr2.push(<MenuItem key="search" onClick={this.handleSearchSubtitleAsset}>{t("Common.MenuAction.VideoSearchSubtitles")}</MenuItem>);
                arr2.push(<MenuItem key="export" onClick={this.handleViewSubtitleAsset}>{t("Common.MenuAction.VideoSubtitles")}</MenuItem>);
            }
            else if (metadata?.hasSubtitles ?? false) {
                arr2.push(<MenuItem key="search" onClick={this.handleSearchSubtitleMeta}>{t("Common.MenuAction.VideoSearchSubtitles")}</MenuItem>);
                arr2.push(<MenuItem key="export" onClick={this.handleViewSubtitleMeta}> {t("Common.MenuAction.VideoSubtitles")}</MenuItem >);
            }
        }

        if (permissions.canEdit && attachs?.length > 0 && attachs[0].mainFile.mimeType.startsWith("video")) {
            arr2.push(<MenuItem key="download" onClick={this.handleDownloadAsset}><MuiColor variant="info">{t("Common.MenuAction.DownloadSourceVideo")}</MuiColor></MenuItem>);
        }

        if (arr2.length > 0) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="dividerVideo" />);
            actionMenu.push(arr2);
        }

        //
        const arr4 = [];

        if (this.localInteraction.publicationTypeAllowInteraction)
            arr4.push(<MenuItem key="likeReview" onClick={this.handleLikeReviewClick}>{t("Common.MenuAction.LikeReview")}</MenuItem>);

        if (arr4.length > 0) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="dividerLike" />);
            actionMenu.push(arr4);
        }

        //
        const arr3 = [];

        if (publicationType == PublicationType.Post || publicationType == PublicationType.List)
            arr3.push(<MenuItem key="question" onClick={this.handleCreateQuestion}>{t("Common.MenuAction.CreateQuestion")}</MenuItem>);

        if (this.localPub.parentId == null) {
            //arr3.push(<MenuItem key="qrcode" onClick={() => this.handleQrCode(publicationId)}>{t("Common.MenuAction.QrCode")}</MenuItem>);
            arr3.push(<MenuItem key="copylink" onClick={this.handleCopyLink}>{t("Common.MenuAction.CopyLink")}</MenuItem>);
        }
        if (onInfoRequest) {
            arr3.push(<MenuItem key="info" onClick={this.handleInfoRequest}>{t("Common.MenuAction.Info")}</MenuItem>);
        }

        if (arr3.length > 0) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="dividerInfo" />);
            actionMenu.push(arr3);
        }

        //
        if (this.localPermissions.canEdit) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="dividerAudit" />);
            actionMenu.push(<MenuItem key="audit" onClick={this.handleAudit}>{t("Common.MenuAction.Audit")}</MenuItem>);
        }

        if (onRemovePublication) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="dividerDelete" />);
            actionMenu.push(<MenuItem key="delete" onClick={this.handleDelete}><MuiColor variant="error">{t("Common.MenuAction.Delete")}</MuiColor></MenuItem>);
        }

        //
        return actionMenu;
    }

    //
    public render() {
        const {
            data: {
                publicationId,
                publication,
                permissions,
                interaction,
                bestAnswer
            },
            fullView
        } = this.props;

        this.handlePublicationId = fullView || bestAnswer == null ? publicationId : bestAnswer.publicationId;
        this.localPermissions = fullView || bestAnswer == null ? permissions : bestAnswer.permissions;
        this.localInteraction = fullView || bestAnswer == null ? interaction : bestAnswer.interaction;
        this.localPub = fullView || bestAnswer == null ? publication : bestAnswer.publication;

        const { menuAnchorEl, dialog } = this.state;
        const actionMenu = this.buildDropDownMenu();

        return (
            <>
                {dialog}

                <ButtonEllipsis
                    onClick={this.handleOpenMenu}
                />
                <Menu
                    anchorEl={menuAnchorEl}
                    open={menuAnchorEl != null}
                    onClose={() => { this.handleCloseMenu(); }}
                >
                    {actionMenu}
                </Menu>
            </>
        );
    }
}

export default withSnackbar(withTranslation()(ActionsDefaultDropDown));
